import { useEffect, useRef, useState } from "react";
import DateUtils from "../../../../common/utils/dateUtils";
import { IDLE_INTERVAL_CHECK_SECONDS, LOCAL_STORAGE_LAST_ACTIVITY_KEY, IDLE_WARNING_OFFSET_MINUTES } from "../../../../common/constants";
import mainConfig from "../../../../common/config/main.config";
import { useSelector } from "react-redux";
import { offlineSelector } from "../../../../redux/metadata/selectors/metadata.selectors";

export const useIdleCheck = () => {
  const [isIdleModalShown, setIdleModalShown] = useState(false);
  const idleTimer = useRef<NodeJS.Timeout | null>(null);
  const isOffline = useSelector(offlineSelector);

  const getMaxTimeoutMinutes = () => {
    const sec = Number(window?.authSettings?.inactiveTimeoutInSeconds);
    if (!Number.isNaN(sec) && sec > 0) return Math.floor(sec / 60);
    return mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).timeoutBeforeLogoutInMinutes;
  };

  const clearIdleInterval = () => {
    if (idleTimer.current) {
      clearInterval(idleTimer.current);
      idleTimer.current = null;
    }
  };

  const checkForIdleness = () => {
    const lastActiveTime = localStorage.getItem(LOCAL_STORAGE_LAST_ACTIVITY_KEY);
    if (!lastActiveTime) return;

    const differenceInMinutes = DateUtils.getDifferenceInMinutes(new Date(+lastActiveTime), new Date());
    const maxTimeout = getMaxTimeoutMinutes();
    const warnAt = Math.max(0, maxTimeout - IDLE_WARNING_OFFSET_MINUTES);

    if (differenceInMinutes >= warnAt) {
      clearInterval(idleTimer.current!);
      setIdleModalShown(true);
    }
  };

  const startIdleInterval = () => {
    if (idleTimer.current) return;
    idleTimer.current = setInterval(checkForIdleness, IDLE_INTERVAL_CHECK_SECONDS * 1000);
  };

  const setupTimer = () => {
    if (isOffline) {
      clearInterval(idleTimer.current!);
      setIdleModalShown(false);
      return;
    }
    startIdleInterval();
  };

  useEffect(() => {
    setupTimer();
    return () => clearIdleInterval();
  }, [isOffline]);

  const continueSession = () => {
    setIdleModalShown(false);
    startIdleInterval();
  };

  return { isIdleModalShown, continueSession };
};
